import { Button, Card, Spin, Typography } from "antd";
import { Download, Eye, FileText } from "lucide-react";
import React from "react";
import { TFile } from "~/types";
import { RESPONSIVE_CLASSES } from "~/utils/responsive";

const { Text } = Typography;

interface DocumentListProps {
  documents: TFile[];
  isLoading: boolean;
  onView: (doc: TFile) => void;
  onDownload: (doc: TFile) => void;
}

export const DocumentList: React.FC<DocumentListProps> = ({
  documents,
  isLoading,
  onView,
  onDownload,
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();

    if (date.toDateString() === now.toDateString()) {
      return `${date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })} Today`;
    }

    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <Card classNames={{ body: "p-0" }}>
      <div className="p-3 sm:p-4 border-b">
        <Text strong className={RESPONSIVE_CLASSES.bodyText}>Recent Documents</Text>
      </div>
      {isLoading ? (
        <div className="p-6 flex justify-center">
          <Spin />
        </div>
      ) : (
        <div className="divide-y">
          {documents.length > 0 ? (
            documents.map((doc) => (
              <div
                key={doc.id}
                className="p-3 sm:p-4 hover:bg-gray-50 transition-colors"
              >
                {/* Desktop Layout */}
                <div className={`${RESPONSIVE_CLASSES.showOnDesktop} flex justify-between items-center`}>
                  <div className="flex items-center">
                    <FileText size={16} className="mr-3 text-blue-600 flex-shrink-0" />
                    <div>
                      <Text strong className={RESPONSIVE_CLASSES.bodyText}>{doc.title}</Text>
                      <Text type="secondary" className={`${RESPONSIVE_CLASSES.captionText} block`}>
                        {formatDate(doc.createdAt)}
                      </Text>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      type="text"
                      icon={<Eye size={16} />}
                      onClick={() => onView(doc)}
                    />
                    <Button
                      type="text"
                      icon={<Download size={16} />}
                      onClick={() => onDownload(doc)}
                    />
                  </div>
                </div>

                {/* Mobile/Tablet Layout */}
                <div className={RESPONSIVE_CLASSES.hideOnDesktop}>
                  <div className="flex items-start space-x-3">
                    <FileText size={16} className="text-blue-600 flex-shrink-0 mt-1" />
                    <div className="flex-1 min-w-0">
                      <Text strong className={`${RESPONSIVE_CLASSES.bodyText} block mb-1`}>{doc.title}</Text>
                      <Text type="secondary" className={`${RESPONSIVE_CLASSES.captionText} block mb-2`}>
                        {formatDate(doc.createdAt)}
                      </Text>
                      <div className="flex space-x-3">
                        <Button
                          type="text"
                          size="small"
                          icon={<Eye size={14} />}
                          onClick={() => onView(doc)}
                          className={RESPONSIVE_CLASSES.captionText}
                        >
                          View
                        </Button>
                        <Button
                          type="text"
                          size="small"
                          icon={<Download size={14} />}
                          onClick={() => onDownload(doc)}
                          className={RESPONSIVE_CLASSES.captionText}
                        >
                          Download
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="p-6 text-center text-gray-400">
              <FileText size={24} className="mx-auto mb-2" />
              <Text className={RESPONSIVE_CLASSES.bodyText}>No documents found</Text>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};